import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Grid, IconButton, Input, Paper, SelectChangeEvent, Stack } from '@mui/material';
import {
  Cancel as CancelIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as CreateIcon,
} from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../components/table/types';

import {
  useGetMerchantCoinConfigQuery,
  usePutMerchantCoinConfigGoupsMutation,
  PutMerchantDTO,
  useGetMerchantCoinConfigByIdQuery,
  PostMerchantDto,
  usePostMerchantCoinConfigGoupsMutation,
  useDeleteMerchantCoinConfigGoupsMutation,
} from '../../../api/controllers/merchant';
import { layoutAction } from '../../../store/sideBarStore/layoutStore';
import { useAppActions } from '../../../utils/hooks';
import { CreateMerchantCoinConfigModal } from './components/CreateMerchantCoinConfigModal';
import Title from '../../../components/title/Title';
import { DeleteMerchantCoinConfigModal } from './components/DeleteMerchantCoinConfigModal';
import { formatAmount } from '../../../utils';

export const MerchantCoinConfig: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { profileUid } = useParams<'profileUid'>();

  // api data
  const {
    data: merchantCoinConfigs,
    isLoading: isMerchantCoinConfigLoading,
    refetch,
  } = useGetMerchantCoinConfigByIdQuery({ profileUid: profileUid ?? '' });
  const { data: defaultValues } = useGetMerchantCoinConfigQuery();
  const [updateMerchant, { isLoading: isUpdateMerchantLoading }] =
    usePutMerchantCoinConfigGoupsMutation();
  const [createMerchant, { isLoading: isCreateMerchantpLoading }] =
    usePostMerchantCoinConfigGoupsMutation();
  const [deleteMerchent, { isLoading: isDeleteMerchantLoading }] =
    useDeleteMerchantCoinConfigGoupsMutation();

  // edit fields logic
  const [editFields, setEditFields] = useState<PostMerchantDto['body'] | null>(null);

  const handleEditFields = (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState<number | null>();
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (merchant: PostMerchantDto['body'], id: number) => {
    setEditFields(merchant);
    setActiveRowId(id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveRowId(null);
    setEditMode(false);
  };

  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (id: number) => {
    setDeleteModalShown(true);
    setActiveRowId(id);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRowId(null);
  };

  const [createModalShown, setCreateModalShown] = useState(false);

  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setActiveRowId(null);
    setEditFields(null);
  };

  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    setSpeedDialButtonComponent([
      { icon: <CreateIcon />, onClick: openCreateModal, disabled: isEditMode },
    ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // edit merchant logic
  const editMerchant = async () => {
    if (!editFields || !editFields.ttl_seconds || !activeRowId) return;

    const body: PutMerchantDTO['body'] = {
      ttl_seconds: Number(editFields.ttl_seconds),
      min_amount: String(editFields.min_amount),
    };

    try {
      await updateMerchant({ merchant_coin_config_id: activeRowId, body }).unwrap();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      offEditMode();
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  // create fee handler
  const createFeeGroup = async () => {
    if (!editFields) return;

    const args = {
      profile_uid: profileUid ?? '',
      body: {
        coin_id: editFields.coin_id,
        ttl_seconds: Number(editFields.ttl_seconds),
        min_amount: editFields.min_amount,
      },
    };

    try {
      await createMerchant(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete fee handler
  const deleteMerchendantHandler = async () => {
    if (!activeRowId) return;

    const args = {
      merchant_coin_config_id: activeRowId,
      profile_uid: profileUid ?? '',
    };

    try {
      await deleteMerchent(args).unwrap();
      closeDeleteModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('merchant-coin-configs.head-cells.id') },
    { id: '2', label: t('merchant-coin-configs.head-cells.coin-name') },
    { id: '3', label: t('merchant-coin-configs.head-cells.ttl-seconds') },
    { id: '4', label: t('merchant-coin-configs.head-cells.min-amount') },
    { id: '5', label: t('merchant-coin-configs.head-cells.actions') },
  ];
  const defaultValuesHeadCells: HeadCell[] = [
    { id: '1', label: t('merchant-coin-configs.head-cells.id') },
    { id: '2', label: t('merchant-coin-configs.head-cells.coin-name') },
    { id: '3', label: t('merchant-coin-configs.head-cells.ttl-seconds') },
    { id: '4', label: t('merchant-coin-configs.head-cells.min-amount') },
  ];

  const rows: Row[] | undefined = merchantCoinConfigs?.configs?.map((it) => {
    const {
      id,
      coin: { name },
      ttl_seconds,
      min_amount,
    } = it;

    const cells: RowCell[] = [
      { name: 'id', value: id },
      {
        name: 'coin_name',
        value: name,
      },
      {
        name: 'ttl_seconds',
        value: ttl_seconds,
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="ttl_seconds"
              type="number"
              value={editFields?.ttl_seconds ?? ''}
              onChange={(e) => handleEditFields(e)}
              disabled={isUpdateMerchantLoading}
            />
          ) : undefined,
      },
      {
        name: 'min_amount',
        value: formatAmount(min_amount),
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="min_amount"
              type="number"
              value={editFields?.min_amount ?? ''}
              onChange={(e) => handleEditFields(e)}
              disabled={isUpdateMerchantLoading}
            />
          ) : undefined,
      },
      {
        name: 'actions',
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={editMerchant}
                disabled={isUpdateMerchantLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={offEditMode}
                disabled={isUpdateMerchantLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={(e) => {
                startEditMode(it, it.id);
              }}
              disabled={isMerchantCoinConfigLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              color="error"
              onClick={(e) => {
                openDeleteModal(it.id);
              }}
              disabled={isDeleteMerchantLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return {
      cells,
      hover: false,
    };
  });

  const defaultValuesRows: Row[] | undefined = defaultValues?.configs?.map((it) => {
    const {
      id,
      coin: { name },
      ttl_seconds,
      min_amount,
    } = it;

    const cells: RowCell[] = [
      { name: 'id', value: id },
      {
        name: 'coin_name',
        value: name,
      },
      {
        name: 'ttl_seconds',
        value: ttl_seconds,
      },
      {
        name: 'min_amount',
        value: formatAmount(min_amount),
      },
    ];

    return {
      cells,
      hover: false,
    };
  });
  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Title>{t('profile-merchant-coin-config.overwritten-values')}</Title>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
        <Title>{t('profile-merchant-coin-config.default-values')}</Title>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={defaultValuesHeadCells} rows={defaultValuesRows} />
        </Paper>
      </Grid>

      <DeleteMerchantCoinConfigModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteMerchendantHandler}
        isLoading={isDeleteMerchantLoading}
      />

      <CreateMerchantCoinConfigModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createFeeGroup}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isCreateMerchantpLoading}
      />
    </>
  );
};
